import { environment } from 'environments/environment';
import apiService from 'services/ApiService';
import { User, UserAccount } from 'types/user.types';
import { logger } from 'utils/logger';
import { BehaviorSubject, Observable } from 'rxjs';

// Create a service-specific logger
const log = logger.getLogger('UserService');

// API endpoints
const API_ENDPOINTS = {
    baseUrl: environment.services.account.baseUrl,
    accountUrl: environment.services.account.baseUrl + environment.services.account.basePath,
    currentUserProfile: '/users/current/profile',
    userProfile: (id: string) => `/users/${id}`
};

/**
 * Service for managing user data and interactions with the user API
 */
class UserService {
    private userProfileSubject = new BehaviorSubject<User | null>(null);
    
    /**
     * Set the basic user profile (typically from Auth0)
     * This will be called by AuthService when authentication occurs
     */
    setUserBasicProfile(user: User | null): void {
        if (!user) {
            this.clearUser();
            return;
        }
        
        if (user.id) {
            // Store the basic profile first
            this.userProfileSubject.next(user);
            
            // Then enrich it with account details
            this.enrichUserWithAccountProfile(user.id)
                .catch(err => log.error('Failed to enrich user with account profile', err));
        }
    }
    
    /**
     * Get the user as an observable that will update when the profile changes
     */
    getUser$(): Observable<User | null> {
        return this.userProfileSubject.asObservable();
    }
    
    /**
     * Get the current user synchronously
     */
    getUser(): User | null {
        return this.userProfileSubject.value;
    }
    
    /**
     * Enrich user profile with account details from the API
     */
    async enrichUserWithAccountProfile(id: string): Promise<void> {
        try {
            const currentUser = this.userProfileSubject.value;
            if (!currentUser) {
                log.error('Cannot enrich user - no current user set');
                return;
            }
            
            const accountProfile = await this.getUserProfile();
            
            // Update the user with account profile data
            const enrichedUser: User = {
                ...currentUser,
                accountProfile
            };
            
            // Update the user subject with the enriched data
            this.userProfileSubject.next(enrichedUser);
            log.log('User profile enriched with account details');
            
        } catch (error) {
            log.error('Error enriching user with account profile:', error);
            throw error;
        }
    }

    /**
     * Get user profile by ID from the specified endpoint
     */
    async getUserProfileById(id: string): Promise<UserAccount> {
        try {
            const url = `${API_ENDPOINTS.accountUrl}${API_ENDPOINTS.userProfile(id)}`;
            log.log(`Fetching user profile from: ${url}`);
            
            // Skip cache for this request to ensure fresh data
            const response = await apiService.makeRequest('get', url, undefined, false);
            return response.data;
        } catch (error) {
            log.error(`Error fetching user profile for ID ${id}:`, error);
            throw error;
        }
    }

    /**
     * Get current user profile from account API
     * Includes retry logic to handle potential timing issues with token setup
     */
    async getUserProfile(retryCount = 0, maxRetries = 3): Promise<UserAccount> {
        try {
            const url = `${API_ENDPOINTS.accountUrl}${API_ENDPOINTS.currentUserProfile}`;
            log.log(`Fetching user profile from: ${url}, attempt ${retryCount + 1}`);
            
            // Use caching for profile requests to prevent duplicates
            const response = await apiService.makeRequest('get', url, undefined, true);
            return response;
        } catch (error: any) {
            // If we get a 401 and haven't exceeded max retries, wait and try again
            if (error?.response?.status === 401 && retryCount < maxRetries) {
                log.warn(`Received 401 for profile request, retrying in ${(retryCount + 1) * 500}ms...`);
                
                // Exponential backoff - wait longer with each retry
                await new Promise(resolve => setTimeout(resolve, (retryCount + 1) * 500));
                
                // Try again with incremented retry count
                return this.getUserProfile(retryCount + 1, maxRetries);
            }
            
            log.error('Error fetching user profile:', error);
            throw error;
        }
    }
    
    /**
     * Clear the user data (called during logout)
     */
    clearUser(): void {
        this.userProfileSubject.next(null);
    }
}

const userService = new UserService();
export default userService;